import { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  MapPin, 
  Calendar, 
  AlertTriangle,
  MessageCircle,
  Flame,
  CloudRain,
  Zap,
  Wrench,
  ExternalLink,
} from 'lucide-react';
import { GoAlertFill } from 'react-icons/go';
import { Button } from '../ui/button';
import LikeButton from '../LikeButton/LikeButton';
import PostActionsMenu from '../PostActionsMenu/PostActionsMenu';
import PostStatusBadge from '../PostStatusBadge/PostStatusBadge';
import DeleteConfirmationModal from '../DeleteConfirmationModal/DeleteConfirmationModal';
import { useInitiateConversation } from '../../hooks/messages/useMessages';
import usePostActions from '../../hooks/posts/usePostActions';
import { FEED_TYPES } from '../../hooks/notifications/useCombinedFeed';
import { formatDate } from '../../hooks/utils';
import { canExtendPost, EXTEND_WINDOW_DAYS } from '../../utils/postLifecycle';


/**
 * Maps feed item type to the corresponding itemType for LikeButton
 */
const getItemType = (type) => {
  switch (type) {
    case FEED_TYPES.LOST:
      return 'LostAnimal';
    case FEED_TYPES.FOUND:
      return 'FoundAnimal';
    case FEED_TYPES.PSA:
      return 'PSA';
    case FEED_TYPES.PET_SERVICE:
      return 'PetService';
    default:
      return null;
  }
};

/**
 * Get icon for emergency category
 */
const getEmergencyIcon = (category) => {
  const categoryLower = category?.toLowerCase() || '';
  if (categoryLower.includes('wildfire') || categoryLower.includes('fire')) {
    return <Flame className="w-4 h-4 text-orange-600" />;
  }
  if (categoryLower.includes('flood') || categoryLower.includes('storm') || categoryLower.includes('rain')) {
    return <CloudRain className="w-4 h-4 text-blue-600" />;
  }
  if (categoryLower.includes('severe') || categoryLower.includes('thunder')) {
    return <Zap className="w-4 h-4 text-yellow-600" />;
  }
  return <AlertTriangle className="w-4 h-4 text-red-600" />;
};

const OwnerLifecyclePill = ({ item }) => {
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="flex items-center pt-2"
    >
      <PostStatusBadge post={item} />
    </div>
  );
};

const ContactButton = ({ userId, postData, isAuthenticated, onUnauthClick, className = '' }) => {
  const navigate = useNavigate();
  const { initiateConversation } = useInitiateConversation(navigate);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      onUnauthClick(e);
      return;
    }
    initiateConversation(userId, postData);
  };

  return (
    <Button 
      className={`bg-primary hover:bg-primary/90 text-primary-foreground text-xs sm:text-sm 
                  gap-1.5 font-medium w-2/5 min-w-[120px] ${className}`}
      onClick={handleClick}
    >
      <MessageCircle className="w-3.5 h-3.5" />
      Contact
    </Button>
  );
};

const FeedCard = ({
  item,
  onContactClick,
  onEditPost,
  onExtendClick,
  showOwnerLifecycle = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { deletePost, toggleReunited, isDeleting } = usePostActions();

  const handleCardClick = () => {
    navigate(`/post/${item.id}`, {
      state: { postType: item.type },
    });
  };

  const handleContact = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (onContactClick) {
      onContactClick(item);
    }
  };

  const handleEdit = () => {
    if (onEditPost) {
      onEditPost(item);
    } else {
      navigate(`/post/${item.id}/edit`);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await deletePost(item.id, item.type);
    if (result.success) {
      setShowDeleteModal(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleMarkReunited = async () => {
    await toggleReunited(item.id, item.type, item.reunited);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/post/${item.id}`;
    const title = item.title || item.animalName || t('notification', 'Notification');
    
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(url);
        }
      }
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  // Determine if current user owns this post
  const isOwnPost = currentUser && item.userId === currentUser._id;

  const shouldShowOwnerPill =
    showOwnerLifecycle && isOwnPost && !!item.expiresAt;
  const ownerPillSlot = shouldShowOwnerPill ? (
    <OwnerLifecyclePill item={item} />
  ) : null;

  const extendEligibility =
    isOwnPost && item.expiresAt ? canExtendPost(item) : null;
  const extendMenuHandler =
    isOwnPost && item.expiresAt && typeof onExtendClick === 'function'
      ? () => onExtendClick(item)
      : undefined;
  const extendMenuDisabled = extendEligibility
    ? !extendEligibility.allowed
    : false;
  const extendMenuTooltip = extendMenuDisabled
    ? t(
        'extend_post_disabled_tooltip',
        'You can extend within {{count}} days of expiration, or any time after it has expired.',
        { count: EXTEND_WINDOW_DAYS },
      )
    : undefined;

  // Build post context for conversation initiation
  const isLostItem = item.type === FEED_TYPES.LOST;
  const isFoundItem = item.type === FEED_TYPES.FOUND;
  const postData = {
    postId: item.id,
    postType: item.type,
    postName: item.animalName || item.title || '',
    postBreed: item.breed || '',
    postImageUrl: item.imageUrl || '',
    postLocation: isLostItem ? (item.lastSeen || '') : isFoundItem ? (item.foundNear || '') : '',
    postDate: isLostItem ? (item.dateLost || item.createdAt || '') : isFoundItem ? (item.dateFound || item.createdAt || '') : (item.createdAt || ''),
  };

  // Render PSA Card
  if (item.type === FEED_TYPES.PSA) {
    const hasImage = !!item.imageUrl;
    
    return (
      <>
        <div 
          onClick={handleCardClick}
          className={`bg-white rounded-xl border border-border overflow-hidden cursor-pointer 
                     hover:shadow-md transition-all duration-200 hover:border-red-200
                     ${hasImage ? 'sm:flex sm:flex-row' : ''}`}
        >
        {/* PSA */}
        {hasImage && (
          <div className="relative bg-gray-100 sm:w-36 md:w-44 lg:w-48 sm:flex-shrink-0">
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-full aspect-[4/3] sm:aspect-auto sm:h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute top-2 left-2 sm:top-3 sm:left-3">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold shadow-sm bg-amber-100 text-amber-700">
                <GoAlertFill className="w-4 h-4" />
                {t('psa_short', 'PSA')}
              </span>
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col">
          {!hasImage && (
            <div className="bg-gradient-to-r from-amber-50 to-amber-50/50 px-4 py-2.5 border-b border-amber-100">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center">
                  <GoAlertFill className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-xs font-semibold text-amber-800 tracking-wide">
                  {t('psa_short', 'Public Service Announcement')}
                </span>
              </div>
            </div>
          )}

          {/* PSA Content */}
          <div className="p-4 space-y-3 flex-1">
            {/* Title, Bookmark, Share, and Actions row */}
            <div className="flex items-start gap-3">
              <h3 className="font-semibold text-foreground flex-1 text-base leading-snug">
                {item.title}
              </h3>
              {/* Bookmark and Post Actions */}
              <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 flex-shrink-0">
                <LikeButton postId={item.id} itemType={getItemType(item.type)} />
                <PostActionsMenu
                  isOwner={isOwnPost}
                  postUserId={item.userId}
                  postId={item.id}
                  postType={item.type}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onShare={handleShare}
                  onExtend={extendMenuHandler}
                  extendDisabled={extendMenuDisabled}
                  extendDisabledTooltip={extendMenuTooltip}
                  showReunitedOption={false}
                />
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
              {item.description}
            </p>
            
            {/* Meta info */}
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground pt-1">
              {item.locationName && (
                <>
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="line-clamp-1">{item.locationName}</span>
                  <span className="text-border">·</span>
                </>
              )}
              <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{formatDate(item.createdAt)}</span>
            </div>

            {ownerPillSlot}
          </div>
        </div>
        </div>
        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={isDeleting}
          postTitle={item.title}
        />
      </>
    );
  }

  // Render Lost/Found Animal Card
  if (item.type === FEED_TYPES.LOST || item.type === FEED_TYPES.FOUND) {
    const isLost = item.type === FEED_TYPES.LOST;
    const dateField = isLost ? item.dateLost : item.dateFound;
    const locationField = isLost ? item.lastSeen : item.foundNear;
    const hasImage = !!item.imageUrl;
    
    // Card with Image
    if (hasImage) {
      return (
        <>
          <div 
            onClick={handleCardClick}
            className={`bg-white rounded-xl border border-border overflow-hidden cursor-pointer 
                       hover:shadow-md transition-all duration-200 relative
                       ${isLost ? 'hover:border-red-200' : 'hover:border-green-200'}
                       sm:flex sm:flex-row`}
          >
          {/* Animal Image */}
          <div className="relative bg-gray-100 sm:w-36 md:w-44 lg:w-48 sm:flex-shrink-0">
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-full aspect-[4/3] sm:aspect-auto sm:h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            {/* Type Badge */}
            <div className="absolute top-2 left-2 sm:top-3 sm:left-3">
              <span
                className={`text-xs px-2 py-1 rounded-full font-semibold shadow-sm ${
                  item.reunited
                    ? 'bg-green-500 text-white'
                    : isLost 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-green-100 text-green-700'
                }`}
              >
                {item.reunited 
                  ? t('reunited', 'Reunited!') 
                  : isLost 
                    ? t('lost', 'Lost') 
                    : t('found', 'Found')
                }
              </span>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-4 flex-1 flex flex-col justify-between min-h-0">
            {/* Top Section */}
            <div className="space-y-2">
            {/* Title, Bookmark, and Actions */}
            <div className="flex items-start gap-2">
              <h3 className="font-semibold text-foreground flex-1 line-clamp-2 text-base leading-snug">
                {item.title}
              </h3>
              <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 flex-shrink-0">
                <LikeButton postId={item.id} itemType={getItemType(item.type)} />
                <PostActionsMenu
                  isOwner={isOwnPost}
                  postUserId={item.userId}
                  postId={item.id}
                  postType={item.type}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onMarkReunited={handleMarkReunited}
                  onShare={handleShare}
                  onExtend={extendMenuHandler}
                  extendDisabled={extendMenuDisabled}
                  extendDisabledTooltip={extendMenuTooltip}
                  isReunited={item.reunited}
                  showReunitedOption={true}
                />
              </div>
            </div>

              {/* Description */}
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                {item.animalType && item.breed 
                  ? `${item.animalType} • ${item.breed}`
                  : item.animalType || item.breed || t('animal', 'Animal')
                }
                {item.colorMarkings && item.colorMarkings.length > 0 && (
                  <> • {Array.isArray(item.colorMarkings) ? item.colorMarkings.slice(0, 2).join(', ') : item.colorMarkings}</>
                )}
              </p>

              {/* Location & Date */}
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
                {locationField && (
                  <>
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="line-clamp-1">{locationField}</span>
                    <span className="text-border">·</span>
                  </>
                )}
                <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{dateField ? formatDate(dateField) : formatDate(item.createdAt)}</span>
              </div>
            </div>

            {/* Contact Button */}
            {!isOwnPost && item.userId && (
              <div onClick={(e) => e.stopPropagation()} className="mt-3 flex justify-center">
                <ContactButton 
                  userId={item.userId} 
                  postData={postData}
                  isAuthenticated={isAuthenticated}
                  onUnauthClick={handleContact}
                />
              </div>
            )}

            {ownerPillSlot}
          </div>
          </div>
          {/* Delete Confirmation Modal */}
          <DeleteConfirmationModal
            isOpen={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            isDeleting={isDeleting}
            postTitle={item.title}
          />
        </>
      );
    }
    
    // Card without Image
    return (
      <>
        <div 
          onClick={handleCardClick}
          className={`bg-white rounded-xl border border-border overflow-hidden cursor-pointer 
                     hover:shadow-md transition-all duration-200 relative
                     ${isLost ? 'hover:border-red-200' : 'hover:border-green-200'}`}
        >
        {/* Card Content */}
        <div className="p-4 space-y-3">
          {/* Header with Badge, Title, Bookmark, Share, and Actions */}
          <div className="flex items-start gap-2">
            <span
              className={`text-xs px-2 py-1 rounded-full flex-shrink-0 font-semibold ${
                isLost 
                  ? 'bg-red-100 text-red-700' 
                  : 'bg-green-100 text-green-700'
              }`}
            >
              {isLost ? t('lost', 'Lost') : t('found', 'Found')}
            </span>
            
            {/* Title */}
            <h3 className="font-semibold text-foreground flex-1 line-clamp-2 text-base leading-snug">
              {item.title}
            </h3>
            
            {/* Bookmark and Actions */}
            <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 flex-shrink-0">
              <LikeButton postId={item.id} itemType={getItemType(item.type)} />
              <PostActionsMenu
                isOwner={isOwnPost}
                postUserId={item.userId}
                postId={item.id}
                postType={item.type}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onMarkReunited={handleMarkReunited}
                onShare={handleShare}
                onExtend={extendMenuHandler}
                extendDisabled={extendMenuDisabled}
                extendDisabledTooltip={extendMenuTooltip}
                isReunited={item.reunited}
                showReunitedOption={true}
              />
            </div>
          </div>

          {/* Description - compact details */}
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
            {item.animalType && item.breed 
              ? `${item.animalType} • ${item.breed}`
              : item.animalType || item.breed || t('animal', 'Animal')
            }
            {item.colorMarkings && item.colorMarkings.length > 0 && (
              <> • {Array.isArray(item.colorMarkings) ? item.colorMarkings.slice(0, 2).join(', ') : item.colorMarkings}</>
            )}
          </p>

          {/* Location & Date */}
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {locationField && (
              <>
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="line-clamp-1">{locationField}</span>
                <span className="text-border">·</span>
              </>
            )}
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{dateField ? formatDate(dateField) : formatDate(item.createdAt)}</span>
          </div>

          {/* Contact Button */}
          {!isOwnPost && item.userId && (
            <div onClick={(e) => e.stopPropagation()} className="flex justify-center pt-1">
              <ContactButton 
                userId={item.userId} 
                postData={postData}
                isAuthenticated={isAuthenticated}
                onUnauthClick={handleContact}
              />
            </div>
          )}

          {ownerPillSlot}
        </div>
        </div>
        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={isDeleting}
          postTitle={item.title}
        />
      </>
    );
  }

  // Render Pet Service Card
  if (item.type === FEED_TYPES.PET_SERVICE) {
    const hasImage = !!item.imageUrl;
    
    if (hasImage) {
      return (
        <>
          <div 
            onClick={handleCardClick}
            className="bg-white rounded-xl border border-border overflow-hidden cursor-pointer 
                       hover:shadow-md transition-all duration-200 hover:border-purple-200
                       sm:flex sm:flex-row"
          >
            {/* Service Image */}
            <div className="relative bg-gray-100 sm:w-36 md:w-44 lg:w-48 sm:flex-shrink-0">
              <img
                src={item.imageUrl}
                alt={item.title}
                className="w-full aspect-[4/3] sm:aspect-auto sm:h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              {/* Type Badge */}
              <div className="absolute top-2 left-2 sm:top-3 sm:left-3">
                <span className="text-xs px-2 py-1 rounded-full font-semibold shadow-sm bg-purple-100 text-purple-700">
                  {t('pet_service', 'Pet Service')}
                </span>
              </div>
            </div>

            {/* Card Content */}
            <div className="p-4 flex-1 flex flex-col justify-between min-h-0">
              {/* Top Section */}
              <div className="space-y-2">
                {/* Title, Bookmark, and Actions */}
                <div className="flex items-start gap-2">
                  <h3 className="font-semibold text-foreground flex-1 line-clamp-2 text-base leading-snug">
                    {item.title}
                  </h3>
                  <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 flex-shrink-0">
                    <LikeButton postId={item.id} itemType={getItemType(item.type)} />
                    <PostActionsMenu
                      isOwner={isOwnPost}
                      postUserId={item.userId}
                      postId={item.id}
                      postType={item.type}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      onShare={handleShare}
                      onExtend={extendMenuHandler}
                      extendDisabled={extendMenuDisabled}
                      extendDisabledTooltip={extendMenuTooltip}
                      showReunitedOption={false}
                    />
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                  {item.description}
                </p>

                {/* Location & Date */}
                <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  {item.locationName && (
                    <>
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="line-clamp-1">{item.locationName}</span>
                      <span className="text-border">·</span>
                    </>
                  )}
                  <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{formatDate(item.createdAt)}</span>
                </div>
              </div>

              {/* Contact Button */}
              {!isOwnPost && item.userId && (
                <div onClick={(e) => e.stopPropagation()} className="mt-3 flex justify-center">
                  <ContactButton 
                    userId={item.userId} 
                    postData={postData}
                    isAuthenticated={isAuthenticated}
                    onUnauthClick={handleContact}
                  />
                </div>
              )}

              {ownerPillSlot}
            </div>
          </div>
          {/* Delete Confirmation Modal */}
          <DeleteConfirmationModal
            isOpen={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            isDeleting={isDeleting}
            postTitle={item.title}
          />
        </>
      );
    }

    return (
      <>
        <div 
          onClick={handleCardClick}
          className="bg-white rounded-xl border border-border overflow-hidden cursor-pointer 
                     hover:shadow-md transition-all duration-200 hover:border-purple-200"
        >
        {/* Pet Service Header */}
        <div className="bg-gradient-to-r from-purple-50 to-purple-50/50 px-4 py-2.5 border-b border-purple-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center">
              <Wrench className="w-4 h-4 text-purple-600" />
            </div>
            <span className="text-xs font-semibold text-purple-800 tracking-wide">
              {t('pet_service', 'Pet Service')}
            </span>
          </div>
        </div>

        {/* Pet Service Content */}
        <div className="p-4 space-y-3">
          {/* Title, Bookmark, and Actions row */}
          <div className="flex items-start gap-3">
            <h3 className="font-semibold text-foreground flex-1 text-base leading-snug">
              {item.title}
            </h3>
            {/* Bookmark and Post Actions */}
            <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 flex-shrink-0">
              <LikeButton postId={item.id} itemType={getItemType(item.type)} />
              <PostActionsMenu
                isOwner={isOwnPost}
                postUserId={item.userId}
                postId={item.id}
                postType={item.type}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onShare={handleShare}
                onExtend={extendMenuHandler}
                extendDisabled={extendMenuDisabled}
                extendDisabledTooltip={extendMenuTooltip}
                showReunitedOption={false}
              />
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
            {item.description}
          </p>
          
          {/* Meta info */}
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground pt-1">
            {item.locationName && (
              <>
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="line-clamp-1">{item.locationName}</span>
                <span className="text-border">·</span>
              </>
            )}
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{formatDate(item.createdAt)}</span>
          </div>

          {/* Contact Button */}
          {!isOwnPost && item.userId && (
            <div onClick={(e) => e.stopPropagation()} className="flex justify-center pt-1">
              <ContactButton 
                userId={item.userId} 
                postData={postData}
                isAuthenticated={isAuthenticated}
                onUnauthClick={handleContact}
              />
            </div>
          )}

          {ownerPillSlot}
        </div>
        </div>
        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={isDeleting}
          postTitle={item.title}
        />
      </>
    );
  }

  // Render Emergency Alert Card (NASA Events)
  if (item.type === FEED_TYPES.EMERGENCY) {
    const category = item.categories?.[0]?.title || 'Unknown';
    const hasSource = item.sources?.length > 0;

    return (
      <div 
        className="bg-white rounded-xl border border-red-200 overflow-hidden 
                   hover:shadow-md transition-all duration-200"
      >
        {/* Emergency Header */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 px-4 py-2.5 border-b border-red-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                {getEmergencyIcon(category)}
              </div>
              <span className="text-xs font-semibold text-red-800 tracking-wide">
                {t('emergency_alert', 'Emergency Alert')}
              </span>
            </div>
            {/* Category Badge */}
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
              {category}
            </span>
          </div>
        </div>

        {/* Emergency Content */}
        <div className="p-4 space-y-3">
          <h3 className="font-semibold text-foreground text-base leading-snug">
            {item.title}
          </h3>
          
          {item.description && (
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
              {item.description}
            </p>
          )}
          
          {/* Meta info */}
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formatDate(item.createdAt)}</span>
            </div>
            
            {/* Source Link */}
            {hasSource && (
              <a 
                href={item.sources[0].url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
              >
                <span>{t('view_source', 'View Source')}</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {/* NASA Attribution */}
          <div className="text-[10px] text-muted-foreground/60 pt-1 border-t border-border">
            {t('data_from_nasa', 'Data from NASA Earth Observatory')}
          </div>
        </div>
      </div>
    );
  }

  // Fallback for unknown types
  return (
    <div className="bg-white rounded-xl border border-border p-4">
      <p className="text-muted-foreground">{t('unknown_post_type', 'Unknown post type')}</p>
    </div>
  );
};

export default memo(FeedCard);
